import { Component, effect, inject, OnInit, signal } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { FormInputComponent } from '@shared/forms';
import { QuizQuestionsFacade } from '@app/store/quiz-questions';
import { QuizQuestionFacade } from '@app/store/quiz-question';
import { QuizQuestion } from '@app/store/quiz-questions/models/quiz-question';
import { QuizAnswerComponent } from '@app/quiz-questions/quiz-answer/quiz-answer.component';
import { DialogSearchComponent } from '../shared/dialog-search/dialog-search.component';
import { QuestionSelectionService } from './question-selection.service';

type DialogMode = 'select' | 'create';

@Component({
    selector: 'app-add-question-dialog',
    templateUrl: './add-question.component.html',
    styleUrls: ['./add-question.component.css'],
    imports: [ReactiveFormsModule, TranslateModule, ButtonModule, PaginatorModule, FormInputComponent, QuizAnswerComponent, DialogSearchComponent],
})
export class AddQuestionComponent implements OnInit {
    private readonly fb = inject(NonNullableFormBuilder);
    private readonly router = inject(Router);
    private readonly translateService = inject(TranslateService);
    private readonly quizQuestionsFacade = inject(QuizQuestionsFacade);
    private readonly quizQuestionFacade = inject(QuizQuestionFacade);
    private readonly questionSelection = inject(QuestionSelectionService);

    readonly mode = signal<DialogMode>('select');

    readonly quizQuestions = this.quizQuestionsFacade.quizQuestions;
    readonly total = this.quizQuestionsFacade.total;
    readonly listLoading = this.quizQuestionsFacade.loading;
    readonly creating = this.quizQuestionFacade.saving;
    readonly limit = 10;
    readonly first = signal(0);
    readonly searchValue = signal('');

    readonly searchPlaceholder = this.translateService.instant('quizzes.questions.dialog.search');

    readonly form = this.fb.group({
        questionText: this.fb.control('', [FormValidators.Required, FormValidators.Text]),
        answers: this.fb.array([this.createAnswerControl()]),
        correctAnswer: this.fb.control(0),
    });

    get answers() {
        return this.form.controls.answers;
    }

    get correctAnswer() {
        return this.form.controls.correctAnswer;
    }

    constructor() {
        effect(() => {
            const createdId = this.quizQuestionFacade.createdId();
            if (this.quizQuestionFacade.saved() && createdId) {
                const question = this.quizQuestionFacade.quizQuestion(createdId)();
                if (question) {
                    this.questionSelection.select(question);
                    this.quizQuestionFacade.clear();
                    this.close();
                }
            }
        });
    }

    ngOnInit(): void {
        this.loadQuestions();
    }

    setMode(mode: DialogMode): void {
        this.mode.set(mode);
    }

    onSearch(search: string): void {
        this.searchValue.set(search);
        this.first.set(0);
        this.loadQuestions();
    }

    selectQuestion(question: QuizQuestion): void {
        this.questionSelection.select(question);
        this.close();
    }

    onPageChange(event: PaginatorState): void {
        const first = event.first ?? 0;
        this.first.set(first);
        const page = Math.floor(first / this.limit) + 1;
        this.loadQuestions(page);
    }

    loadQuestions(page = 1): void {
        this.quizQuestionsFacade.getQuizQuestions({ page, limit: this.limit, search: this.searchValue() || undefined });
    }

    addAnswer(): void {
        this.answers.push(this.createAnswerControl());
    }

    removeAnswer(index: number): void {
        this.answers.removeAt(index);

        if (this.correctAnswer.value >= this.answers.length) {
            this.correctAnswer.setValue(this.answers.length - 1);
        }
    }

    onCreateSubmit(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }

        this.form.disable();
        const { questionText, answers, correctAnswer } = this.form.getRawValue();

        this.quizQuestionFacade.createQuizQuestion({
            questionText,
            answers: answers.map((answer, index) => ({ value: answer, correct: index === correctAnswer })),
        });
    }

    private createAnswerControl() {
        return this.fb.control('', [FormValidators.Required, FormValidators.Text]);
    }

    close(): void {
        this.router.navigate([{ outlets: { dialog: null } }]);
    }
}
